import { useEffect, useState, useCallback, useMemo, useRef } from 'react'
import { Loader2, ChevronRight, Copy, Check, LogOut, Wallet, ExternalLink, Pencil } from 'lucide-react'
import { useConnect, useConnectors, useAccount, useDisconnect } from 'wagmi'
import { useWallet as useSolanaWallet } from '@solana/wallet-adapter-react'
import { WalletReadyState, type WalletName, type Adapter } from '@solana/wallet-adapter-base'
import { getDisplayError, isUserRejection } from '@/utils/errors'
import { emitWalletEvent, WALLET_EVENT, categorizeWalletError, type WalletChain } from '@/utils/walletTelemetry'
import { QRCodeSVG } from 'qrcode.react'
import { Modal } from '@/components/Modal'
import { WalletLogos, WALLET_LOGO_KEY } from '@/components/icons/WalletLogos'
import { useTronWallet } from '@/hooks/useTronWallet'
import { useThemeColors } from '@/hooks/useThemeColors'
import { FONT_HEADING, FONT_BODY, FONT_MONO } from '@/config/theme'
import { shortAddr } from '@/utils/format'
import { walletDisplayName, appendWalletSuffix } from '@/utils/walletLabel'

interface WalletModalProps {
  isOpen: boolean
  onClose: () => void
  chainFamily: 'evm' | 'solana' | 'tron' | 'all'
  onSelectFamily?: (family: 'evm' | 'solana' | 'tron') => void
  /** When provided (destination/recipient context only), render an "Enter a custom address" row after the
   *  wallet list that hands off to the RecipientModal. Omitted for source connects — you cannot send FROM
   *  an address you don't control, so a custom address is a recipient-only affordance. */
  onEnterCustomAddress?: () => void
}

// Curated display order for EVM connectors
const EVM_DISPLAY_ORDER = ['MetaMask', 'Phantom', 'Coinbase Wallet', 'WalletConnect']

// Wallets that don't inject on a plain mobile browser get a synthetic deep-link row (#39). The deep-link
// opens the dApp inside the wallet's own in-app browser, where it injects its provider(s).
const WALLET_DEEPLINK: Record<'Phantom' | 'MetaMask' | 'Solflare' | 'Backpack', {
  logo: string
  mobile: (url: string, origin: string) => string
  install: string
}> = {
  Phantom: {
    logo: 'phantom',
    // Phantom "browse" universal link — its in-app browser injects BOTH EVM + Solana.
    mobile: (url, origin) => `https://phantom.app/ul/browse/${encodeURIComponent(url)}?ref=${encodeURIComponent(origin)}`,
    install: 'https://phantom.app/download',
  },
  MetaMask: {
    logo: 'metamask',
    // MetaMask dApp deep link — opens the site in MetaMask's in-app browser (EVM + native Solana).
    mobile: (url) => `https://metamask.app.link/dapp/${url.replace(/^https?:\/\//, '')}`,
    install: 'https://metamask.io/download/',
  },
  Solflare: {
    logo: 'solflare',
    // Solflare "browse" universal link (path-encoded url + ref, includes v1 — per Solflare deeplink docs).
    // Opens the dApp in Solflare's in-app browser, where Solflare injects a Wallet-Standard Solana provider
    // (auto-discovered by wallet-adapter) so the user connects natively — no WalletConnect needed. Solana-only.
    mobile: (url, origin) => `https://solflare.com/ul/v1/browse/${encodeURIComponent(url)}?ref=${encodeURIComponent(origin)}`,
    install: 'https://solflare.com/download',
  },
  Backpack: {
    logo: 'backpack',
    // Backpack "browse" universal link (same shape as Solflare — path-encoded url + ref, includes v1).
    // Opens the dApp in Backpack's in-app browser, where Backpack injects a Wallet-Standard Solana provider
    // (auto-discovered) so the user connects natively — no WalletConnect needed. Solana-only.
    mobile: (url, origin) => `https://backpack.app/ul/v1/browse/${encodeURIComponent(url)}?ref=${encodeURIComponent(origin)}`,
    install: 'https://backpack.app/downloads',
  },
}

// Where to send users who click TronLink without the extension installed.
const TRONLINK_INSTALL_URL = 'https://www.tronlink.org/'

const WALLET_DESCRIPTIONS: Record<string, string> = {
  WalletConnect: 'Use any other wallet',
}

/**
 * The connect list's display name.
 *
 * ── ROUTED THROUGH THE SHARED NORMALISER (#261 F3 / ADDENDUM A5), NOT A SECOND TABLE ─────────────
 * This used to be a local four-branch substring match (metamask / phantom / coinbase / walletconnect,
 * else passthrough). F3 added two more consumers of the same question — the review card's recipient
 * label and the SwapCard brand icon — so the mapping now lives once, in `utils/walletLabel.ts`, and
 * this is a thin adapter over it. Ships UNFLAGGED because it is behaviour-preserving, not a change:
 *
 *   · For the four names the old function MAPPED, `walletDisplayName` returns the identical string —
 *     same needles, same lowercase substring test, same canonical spellings.
 *   · The three needles the shared allowlist adds (solflare / backpack / tronlink) cannot alter any
 *     row rendered here: `uniqueConnectors` filters to `EVM_DISPLAY_ORDER`
 *     (`['MetaMask', 'Phantom', 'Coinbase Wallet', 'WalletConnect']`), so a connector normalising to
 *     anything else is dropped before it reaches a logo, a sort slot, or a label — under the old
 *     function AND this one. Every downstream read (`:238-239` sort, `:250-252` filter, `:270`
 *     Phantom probe, `renderEvmRow`'s `displayName`/`logoKey`/`desc`, `evmConnByName`) is fed from
 *     that filtered list.
 *   · Address-shaped input yields no canonical name and falls through to the raw string, exactly as
 *     before — and is filtered out by `EVM_DISPLAY_ORDER` anyway.
 */
function getConnectorDisplayName(name: string): string {
  return walletDisplayName(name)
}

const SectionHeader = ({ label, subtitle, color }: { label: string; subtitle?: string; color: string }) => (
  <div style={{
    padding: '12px 20px 6px', fontSize: 11, fontWeight: 700,
    color, letterSpacing: '0.05em', textTransform: 'uppercase',
    fontFamily: FONT_HEADING,
  }}>
    {label}
    {subtitle && <span style={{ fontWeight: 400, marginLeft: 6, textTransform: 'none' }}>{subtitle}</span>}
  </div>
)

export function WalletModal({
  isOpen,
  onClose,
  chainFamily,
  onSelectFamily,
  onEnterCustomAddress,
}: WalletModalProps) {
  const c = useThemeColors()
  const [connectingId, setConnectingId] = useState<string | null>(null)
  const [connectingName, setConnectingName] = useState<string | null>(null)
  const [wcUri, setWcUri] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  // EVM hooks
  const connectors = useConnectors()
  const { connectAsync } = useConnect()
  const { disconnect: disconnectEvm } = useDisconnect()
  const { isConnected: evmConnected, connector: activeEvmConnector, address: evmAddress } = useAccount()
  const prevEvmConnected = useRef(evmConnected)

  // Solana hooks
  const { wallets: solanaWallets, select: solanaSelect, connect: solanaConnect, connected: solanaConnected, wallet: activeSolanaWallet, disconnect: disconnectSolana } = useSolanaWallet()

  // Tron hooks
  const { connectTron, tronConnected, tronAddress, tronWallets, selectTronWallet, activeTronWallet, disconnectTron } = useTronWallet()

  // Copy state for address badges
  const [copiedAddr, setCopiedAddr] = useState<string | null>(null)
  useEffect(() => {
    if (!copiedAddr) return
    const timer = setTimeout(() => setCopiedAddr(null), 1500)
    return () => clearTimeout(timer)
  }, [copiedAddr])

  // Feed the custom WalletConnect Solana adapter's pairing URI into the SAME QR view the EVM WalletConnect
  // flow uses (#57). The adapter emits a custom 'display_uri' event when UniversalProvider proposes a
  // pairing; mirror the EVM path (which listens on the wagmi connector's provider 'display_uri').
  useEffect(() => {
    const wc = solanaWallets.find(w => w.adapter.name === 'WalletConnect')?.adapter
    if (!wc) return
    const onUri = (uri: string) => setWcUri(uri)
    // eventemitter3 allows arbitrary event names; the WalletAdapterEvents type doesn't include it.
    ;(wc as unknown as { on(e: string, l: (uri: string) => void): void }).on('display_uri', onUri)
    return () => {
      ;(wc as unknown as { off(e: string, l: (uri: string) => void): void }).off('display_uri', onUri)
    }
  }, [solanaWallets])

  // Reset state when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      // Abort an in-flight WalletConnect Solana pairing so its adapter #connecting resets. Otherwise
      // closing the QR view mid-pairing leaves the adapter stuck "connecting", and reopening no-ops
      // behind the connecting guard (handleSolanaConnect early-return) — the QR never re-renders until
      // a full page refresh (#57 follow-up). cancelPendingConnect() is a no-op if nothing is pending.
      const wcAdapter = solanaWallets.find(w => w.adapter.name === 'WalletConnect')?.adapter as
        (Adapter & { cancelPendingConnect?: () => void }) | undefined
      if (wcAdapter?.connecting && !wcAdapter.connected) wcAdapter.cancelPendingConnect?.()
      setConnectingId(null)
      setConnectingName(null)
      setWcUri(null)
      setError(null)
      setTakingLong(false)
      connectingIdRef.current = null
      outcomeHandledRef.current = false
      if (hangTimerRef.current) { clearTimeout(hangTimerRef.current); hangTimerRef.current = null }
      // P0-2: the modal is now dismissible mid-connect, so closing must leave NO orphaned connect state.
      // There is no generic EVM equivalent of the Solana adapter's `cancelPendingConnect` (see the note
      // on `evmConnectPendingRef`), so instead we INVALIDATE the attempt: bumping the id makes any late
      // failure from the in-flight `connectAsync` a no-op against the UI, and drops the retry target.
      evmAttemptIdRef.current += 1
      evmAttemptRef.current = null
      // NOT cleared here, deliberately — `evmConnectPendingRef` is what lets a connect that succeeds
      // AFTER the user dismissed the modal still report the family upward (P0-5).
    } else {
      // ...but that latch is bounded by the closed period. Reopening means the user is picking again, and
      // a connect that never settles (the #128 hang this whole affordance exists for) would otherwise
      // leave the latch armed for the component's lifetime — so the next unrelated `evmConnected` flip
      // would fire onSelectFamily('evm') + onClose() and yank the wallet list out from under them,
      // possibly mid-Solana-pick. A live attempt re-arms it on the very next line of handleEvmConnect.
      evmConnectPendingRef.current = false
    }
  }, [isOpen, solanaWallets])

  // Close modal when Solana connects (terminal success — clears the affordance timer + logs SUCCESS).
  useEffect(() => {
    if (solanaConnected && connectingId?.startsWith('solana-')) {
      if (!outcomeHandledRef.current) {
        outcomeHandledRef.current = true
        emitWalletEvent(WALLET_EVENT.SUCCESS, { chain: 'solana', wallet: connectingNameRef.current })
      }
      if (hangTimerRef.current) { clearTimeout(hangTimerRef.current); hangTimerRef.current = null }
      connectingIdRef.current = null
      onSelectFamily?.('solana')
      onClose()
    }
  }, [solanaConnected, connectingId, onClose, onSelectFamily])

  // Close modal when Tron connects
  useEffect(() => {
    if (tronConnected && connectingId?.startsWith('tron-')) {
      onSelectFamily?.('tron')
      onClose()
    }
  }, [tronConnected, connectingId, onClose, onSelectFamily])

  // Close modal when EVM connects or switches connector.
  //
  // P0-5 — late success after the modal was dismissed. Now that the modal is closable mid-connect (P0-2)
  // and closing clears `connectingId`, gating this effect on `connectingId` alone would silently drop a
  // connect that lands after the close: the wallet IS connected but the app never learns the family.
  // `evmConnectPendingRef` survives the close for exactly that window, so the success is still reported.
  //
  // Firing `onClose()` again on an already-closed modal is safe: both call sites are idempotent setState
  // to the SAME value — BridgePage.tsx:2035 `() => setModalTarget(null)` and ReferralPage.tsx:`() =>
  // setIsWalletModalOpen(false)` — which React bails out of. Verified, not assumed.
  const prevEvmConnectorId = useRef(activeEvmConnector?.id)
  useEffect(() => {
    const isEvmConnecting = connectingId && !connectingId.startsWith('solana-') && !connectingId.startsWith('tron-')
    const hadEvmAttempt = !!isEvmConnecting || evmConnectPendingRef.current
    // The pending flag outlives the modal by design, so this effect can now fire while a DIFFERENT family
    // owns the connect UI — and multichain wallets make that reachable (Phantom connecting for Solana can
    // flip `evmConnected`). Hijacking then would kill the Solana hang timer, hide its recovery affordance,
    // report the wrong family, and close the modal mid-pairing. Never steal the UI from another family.
    const otherFamilyOwnsUi = !!connectingId &&
      (connectingId.startsWith('solana-') || connectingId.startsWith('tron-'))
    const freshConnect = evmConnected && !prevEvmConnected.current
    const connectorSwitch = evmConnected && prevEvmConnected.current && activeEvmConnector?.id !== prevEvmConnectorId.current
    if (hadEvmAttempt && !otherFamilyOwnsUi && (freshConnect || connectorSwitch)) {
      // Terminal success — the outcome is now reflected, so stop tracking it and disarm the affordance.
      evmConnectPendingRef.current = false
      evmAttemptRef.current = null
      if (hangTimerRef.current) { clearTimeout(hangTimerRef.current); hangTimerRef.current = null }
      setTakingLong(false)
      emitWalletEvent(WALLET_EVENT.SUCCESS, { chain: 'evm', wallet: connectingNameRef.current })
      onSelectFamily?.('evm')
      onClose()
    }
    prevEvmConnected.current = evmConnected
    prevEvmConnectorId.current = activeEvmConnector?.id
  }, [evmConnected, activeEvmConnector?.id, connectingId, onClose, onSelectFamily])

  // Sort, deduplicate, and filter connectors to curated list only
  const uniqueConnectors = useMemo(() => {
    const sorted = [...connectors].sort((a, b) => {
      const aName = getConnectorDisplayName(a.name)
      const bName = getConnectorDisplayName(b.name)
      const aIdx = EVM_DISPLAY_ORDER.indexOf(aName)
      const bIdx = EVM_DISPLAY_ORDER.indexOf(bName)
      if (aIdx === -1 && bIdx === -1) return 0
      if (aIdx === -1) return 1
      if (bIdx === -1) return -1
      return aIdx - bIdx
    })

    const seenNames = new Set<string>()
    return sorted.filter(c => {
      const name = getConnectorDisplayName(c.name)
      // Only show curated wallets (filter out "Injected" and other unknowns)
      if (!EVM_DISPLAY_ORDER.includes(name)) return false
      if (seenNames.has(name)) return false
      seenNames.add(name)
      return true
    })
  }, [connectors])

  // ── #39 mobile parity: always-listed Phantom row with a deep-link fallback ──
  // On a plain mobile browser Phantom doesn't inject an EIP-6963 provider, so it's absent from the wagmi
  // connector list — unlike desktop (extension) or Phantom's in-app browser. When there's no injected
  // Phantom connector, show a synthetic Phantom row that deep-links into Phantom's in-app browser (where
  // it injects BOTH EVM + Solana), matching the desktop experience. Deduped: if a real Phantom connector
  // exists we render that instead and skip this fallback.
  const isMobileBrowser = useMemo(
    () => typeof navigator !== 'undefined' && /Android|iPhone|iPad|iPod/i.test(navigator.userAgent),
    [],
  )
  const hasInjectedPhantom = useMemo(
    () => uniqueConnectors.some(c => getConnectorDisplayName(c.name) === 'Phantom'),
    [uniqueConnectors],
  )
  const hasInjectedSolanaPhantom = useMemo(
    () => solanaWallets.some(w => w.adapter.name === 'Phantom'),
    [solanaWallets],
  )
  const hasInjectedSolanaMetaMask = useMemo(
    () => solanaWallets.some(w => w.adapter.name === 'MetaMask'),
    [solanaWallets],
  )
  // Solflare/Backpack inject a Wallet-Standard provider only inside their OWN in-app browser (or as a
  // desktop extension) — absent on a plain mobile browser, so they never appear in solanaWallets there.
  // Show a synthetic deep-link row that "browse"s the dApp into the wallet's in-app browser, where it
  // auto-registers and connects natively. Deduped: when the injected wallet IS present we render that.
  const hasInjectedSolanaSolflare = useMemo(
    () => solanaWallets.some(w => w.adapter.name === 'Solflare'),
    [solanaWallets],
  )
  const hasInjectedSolanaBackpack = useMemo(
    () => solanaWallets.some(w => w.adapter.name === 'Backpack'),
    [solanaWallets],
  )
  const showPhantomFallback = !hasInjectedPhantom && (chainFamily === 'all' || chainFamily === 'evm')
  const showPhantomSolanaFallback = !hasInjectedSolanaPhantom && (chainFamily === 'all' || chainFamily === 'solana')
  const showMetaMaskSolanaFallback = !hasInjectedSolanaMetaMask && (chainFamily === 'all' || chainFamily === 'solana')
  const showSolflareSolanaFallback = !hasInjectedSolanaSolflare && (chainFamily === 'all' || chainFamily === 'solana')
  const showBackpackSolanaFallback = !hasInjectedSolanaBackpack && (chainFamily === 'all' || chainFamily === 'solana')

  const handleWalletFallback = useCallback((wallet: 'Phantom' | 'MetaMask' | 'Solflare' | 'Backpack', chain: 'evm' | 'solana') => {
    emitWalletEvent(WALLET_EVENT.ATTEMPT, { chain, wallet, via: 'deeplink' })
    if (typeof window === 'undefined') return
    const cfg = WALLET_DEEPLINK[wallet]
    if (isMobileBrowser) window.location.href = cfg.mobile(window.location.href, window.location.origin)
    else window.open(cfg.install, '_blank', 'noopener,noreferrer')
  }, [isMobileBrowser])

  // Shared synthetic deep-link row (EVM + Solana). Same handler; per-section + per-wallet telemetry.
  const renderWalletFallbackRow = (wallet: 'Phantom' | 'MetaMask' | 'Solflare' | 'Backpack', chain: 'evm' | 'solana') => {
    const Logo = WalletLogos[WALLET_DEEPLINK[wallet].logo]
    return (
      <button
        key={`fallback-${wallet}-${chain}`}
        onClick={() => handleWalletFallback(wallet, chain)}
        style={{
          display: 'flex', alignItems: 'center', gap: 12, width: '100%',
          padding: '16px 20px', background: 'none', border: 'none',
          cursor: 'pointer', borderBottom: `1px solid ${c.dv}`, fontFamily: FONT_BODY,
        }}
        onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = c.blB }}
        onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = 'none' }}
      >
        <div style={{ width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          {Logo ? <Logo size={40} /> : <Wallet size={26} color={c.mu} />}
        </div>
        <div style={{ flex: 1, textAlign: 'left' }}>
          <div style={{ fontSize: 15, fontWeight: 600, color: c.tx }}>{wallet}</div>
          <div style={{ fontSize: 13, color: c.mu, marginTop: 2 }}>
            {isMobileBrowser ? `Open in ${wallet}` : 'Not installed'}
          </div>
        </div>
        <ChevronRight size={15} style={{ color: c.mu }} />
      </button>
    )
  }

  // ── Connect-hang affordance (shared by Solana #80 and EVM #128/P0-1) ──
  // MOVED (not rewritten) from below the Solana section so `handleEvmConnect` can reference
  // `startHangTimer` in its dependency array — a useCallback dep list is evaluated DURING render, so a
  // later `const` would be in its temporal dead zone. Only `startHangTimer` gained a parameter; the
  // timer, the threshold, and the Solana branch inside it are unchanged.
  const hangTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  // Non-destructive "taking longer than expected" affordance (Retry / Cancel / Reload). NOT an auto-abort:
  // a legitimate WalletConnect connect (scan / app-switch / approve) can exceed this, so we never cancel.
  const [takingLong, setTakingLong] = useState(false)
  const HANG_AFFORDANCE_MS = 18_000

  const clearHangTimer = useCallback(() => {
    if (hangTimerRef.current) { clearTimeout(hangTimerRef.current); hangTimerRef.current = null }
  }, [])

  // `chain` is now explicit so the telemetry reflects the wallet family that actually hung. It was
  // hardcoded 'solana' while this timer was Solana-only (#80); the Solana callers pass 'solana', so their
  // emitted events are identical. The adapter probe stays Solana-gated — `activeSolanaWalletRef` says
  // nothing about an EVM connector and could still hold a stale wallet from an earlier Solana attempt.
  const startHangTimer = useCallback((walletName: string, chain: WalletChain) => {
    clearHangTimer()
    hangTimerRef.current = setTimeout(() => {
      setTakingLong(true)
      emitWalletEvent(WALLET_EVENT.AFFORDANCE_SHOWN, { chain, wallet: walletName, elapsedMs: HANG_AFFORDANCE_MS })
      // If the adapter is STILL connecting past the threshold, it's the rare genuinely-hung provider
      // promise — only a page reload resets a poisoned #connecting (proven: disconnect() can't clear it).
      if (chain === 'solana' && activeSolanaWalletRef.current?.adapter.connecting) {
        emitWalletEvent(WALLET_EVENT.HANG_SUSPECTED, { chain, wallet: walletName })
      }
    }, HANG_AFFORDANCE_MS)
  }, [clearHangTimer])

  // ── EVM connect-attempt bookkeeping (P0-1 / P0-2 / P0-5) ──
  // There is NO generic EVM counterpart to the Solana adapter's `cancelPendingConnect`: wagmi's
  // `connectAsync` exposes no abort, and an injected `eth_requestAccounts` promise can only be settled by
  // the wallet itself. (The Coinbase SDK alone can reject its pending request — see the Cancel handler.)
  // So instead of cancelling we INVALIDATE: every attempt takes a monotonic id, and a late failure from a
  // superseded attempt is discarded rather than repainting a modal the user already moved on from.
  const evmAttemptIdRef = useRef(0)
  // The live attempt's connector + label, so Retry can re-run exactly what the user picked. Null once the
  // attempt is finished, cancelled, or the modal closed.
  const evmAttemptRef = useRef<{ connectorId: string; displayName: string } | null>(null)
  // "An EVM connect is outstanding and its success has not been reported upward yet." Deliberately NOT
  // cleared when the modal closes — that is the whole P0-5 mechanism.
  const evmConnectPendingRef = useRef(false)

  // EVM connect handler
  const handleEvmConnect = useCallback(async (connectorId: string, displayName: string) => {
    // Active connector in 'all' mode — skip connection, fire callback directly
    const isActiveConnector = evmConnected && activeEvmConnector &&
      (activeEvmConnector.id === connectorId || activeEvmConnector.uid === connectorId)
    if (chainFamily === 'all' && isActiveConnector) {
      onSelectFamily?.('evm')
      onClose()
      return
    }

    setConnectingId(connectorId)
    setConnectingName(displayName)
    setError(null)
    setWcUri(null)
    setTakingLong(false)

    const connector = connectors.find(c => c.id === connectorId || c.uid === connectorId)
    if (!connector) {
      // Keep `connectingId` set: the error surfaces live INSIDE the `isConnecting` branch, so nulling it
      // here would drop the user back to the wallet list with the message rendered nowhere — a silent
      // dead-end on the exact click they just made. Show the recovery affordance with the reason instead.
      setError('Connector not found')
      setTakingLong(true)
      return
    }

    // P0-1: arm the same non-destructive hang affordance the Solana path has had since #80. `connectAsync`
    // below has no timeout, no race and no `finally`, so a provider promise that never settles — Coinbase
    // Wallet SDK's `postRequestAndWaitForResponse` when the popup is dismissed or an OS universal link
    // dead-ends, the reported #128 behaviour — otherwise pins `connectingId` forever. We never auto-abort;
    // a legitimate WalletConnect connect can exceed the threshold.
    const attemptId = ++evmAttemptIdRef.current
    evmAttemptRef.current = { connectorId, displayName }
    evmConnectPendingRef.current = true
    emitWalletEvent(WALLET_EVENT.ATTEMPT, { chain: 'evm', wallet: displayName })
    startHangTimer(displayName, 'evm')

    // True while this specific attempt still owns the UI. A close, a cancel, or a newer attempt bumps the
    // id, and everything below then leaves the modal alone (P0-2: no orphaned state after dismissal).
    const isCurrent = () => evmAttemptIdRef.current === attemptId

    try {
      // WalletConnect: listen for display_uri, let useEffect close on connect
      if (connector.id === 'walletConnect') {
        const provider = await connector.getProvider() as any
        if (provider?.on) {
          provider.on('display_uri', (uri: string) => {
            // `wcUri` short-circuits the ENTIRE render into the QR view, above the family guard on the
            // close effect — so an unguarded late emit could swap a live Solana connect screen for an EVM
            // WalletConnect QR. This listener is never removed (pre-existing), so guard the write instead.
            if (!isCurrent()) return
            setWcUri(uri)
          })
        }
        await connectAsync({ connector })
        // Don't call onClose() directly — the useEffect watching evmConnected
        // handles it reliably after wagmi state propagates. `evmConnectPendingRef` stays true so that
        // effect still fires if the user dismissed the modal while the pairing was in flight (P0-5).
        if (isCurrent()) { clearHangTimer(); setTakingLong(false) }
        return
      }

      // Coinbase Wallet: SDK handles its own popup/link flow
      if (connector.id === 'coinbaseWalletSDK') {
        await connectAsync({ connector })
        // Superseded (modal closed, cancelled, or another wallet picked since) — touch nothing. The timer
        // and `takingLong` may now belong to a Solana attempt, and clobbering them would strip that
        // attempt's recovery affordance. `evmConnectPendingRef` stays true so the close effect above still
        // reports this success once nothing else owns the UI (P0-5).
        if (!isCurrent()) return
        clearHangTimer()
        evmConnectPendingRef.current = false  // reported below — don't let the close effect re-fire
        evmAttemptRef.current = null
        setTakingLong(false)
        // This branch deliberately bypasses the close effect, so it has to emit its own terminal SUCCESS —
        // otherwise the funnel would show a Coinbase ATTEMPT that never resolves on the dashboard.
        emitWalletEvent(WALLET_EVENT.SUCCESS, { chain: 'evm', wallet: displayName })
        setConnectingId(null)  // prevent close effect from double-firing
        onSelectFamily?.('evm')
        onClose()
        return
      }

      // For injected wallets (MetaMask, Phantom EVM, etc.):
      // When no wallet is connected, wallet_requestPermissions forces the popup
      // even if the site was already approved. When switching wallets, skip it —
      // the permissions trick can interfere with wagmi's internal connector state.
      //
      // CORRECTION (#137 Phase 1c): this comment used to claim "connectAsync handles disconnect +
      // reconnect". It does NOT. `@wagmi/core`'s connect() never calls the previous connector's
      // disconnect() — its only two "disconnect" mentions are an event-listener registration
      // (connect.js:21) and a status string (connect.js:50). Switching wallets therefore leaves the
      // outgoing wallet's provider state in place, including Coinbase's persisted `SignerType`
      // localStorage key, which its own CoinbaseWalletProvider.disconnect() is the sole clearer of.
      // That stale key is what used to misroute a later non-Coinbase EOA to bridge(); it is now
      // gated on the active connector in `utils/smartAccount.ts`. Do not re-derive a "wagmi cleans
      // up on connect" assumption from this block — it does not, and code has already been written
      // on that false premise once.
      if (!evmConnected) {
        const provider = await connector.getProvider() as any
        if (provider?.request) {
          await provider.request({ method: 'wallet_requestPermissions', params: [{ eth_accounts: {} }] })
        }
      }

      await connectAsync({ connector })
      // Same supersession rule as the Coinbase branch above — never repaint a UI this attempt no longer
      // owns; the close effect reports the success instead.
      if (!isCurrent()) return
      clearHangTimer()
      evmConnectPendingRef.current = false
      evmAttemptRef.current = null
      setTakingLong(false)
      onSelectFamily?.('evm')
      onClose()
    } catch (e: any) {
      // P0-3. The old test was `e?.code === 4001 || e?.message?.includes('rejected')`. The second clause is
      // an UNANCHORED substring match over an arbitrary message, so any failure whose text merely contains
      // "rejected" — RPC errors, provider errors, upstream text we don't control — was silently discarded
      // and the user landed back on the wallet list with no explanation. `isUserRejection`
      // (utils/errors.ts:282) is the app-wide classifier: EIP-1193 4001, ethers' ACTION_REJECTED, and
      // ANCHORED phrases ("user rejected", "user denied", "rejected the request"…) — never a bare
      // "rejected". viem's UserRejectedRequestError carries code 4001, so wagmi rejections still match.
      const rejected = isUserRejection(e)
      emitWalletEvent(rejected ? WALLET_EVENT.USER_REJECTED : categorizeWalletError(e), {
        chain: 'evm', wallet: displayName, errorName: e?.name ?? null,
      })
      // Clear the pending flag BEFORE the supersession check. A failure is terminal: there is no success
      // left to report, so the flag must not stay armed. Leaving it set here (the ordering bug this
      // comment replaces) left it true for the component's lifetime after "click wallet → dismiss modal →
      // dismiss wallet popup", and the close effect above would then latch onto an unrelated later
      // connect. Supersession only gates the UI writes below, never the bookkeeping.
      evmConnectPendingRef.current = false
      if (!isCurrent()) return  // superseded attempt — recorded above, but never repaints the UI
      evmAttemptRef.current = null
      clearHangTimer()
      setWcUri(null)
      if (rejected) {
        // A GENUINE user rejection stays silent by design — the user knows what they just declined and
        // does not need to be told. Straight back to the wallet list, no banner.
        setConnectingId(null)
        setConnectingName(null)
        setTakingLong(false)
        return
      }
      // Everything else is a real failure and MUST be visible. `connectingId` deliberately stays set:
      // the error surfaces render only inside the `isConnecting` branch, so nulling it here would have
      // made `setError` unobservable. Mirrors the Solana handler's #80 fix.
      setError(getDisplayError(e))
      setTakingLong(true)
    }
  }, [connectors, connectAsync, onClose, chainFamily, evmConnected, activeEvmConnector, onSelectFamily, startHangTimer, clearHangTimer])

  // Refs for connect functions — avoids re-triggering effects when the function
  // reference changes (which happens every render for wallet-adapter hooks)
  const solanaConnectRef = useRef(solanaConnect)
  solanaConnectRef.current = solanaConnect
  const connectTronRef = useRef(connectTron)
  connectTronRef.current = connectTron

  // Guards to prevent double-firing connect() per attempt
  const tronConnectFired = useRef(false)

  // ── #80 Solana connect-hang: de-race + observable, non-destructive recovery ──
  // Refs mirror async React state so event handlers / timers read live values (state lags a render).
  const connectingIdRef = useRef<string | null>(null)
  const connectingNameRef = useRef<string | null>(null)
  connectingNameRef.current = connectingName
  const solanaConnectedRef = useRef(solanaConnected)
  solanaConnectedRef.current = solanaConnected
  const activeSolanaWalletRef = useRef(activeSolanaWallet)
  activeSolanaWalletRef.current = activeSolanaWallet
  // Dedupe terminal outcomes across the two error channels (adapter 'error' event + connect().catch).
  const outcomeHandledRef = useRef(false)

  // Clear the Solana connecting UI. Idempotent. Does NOT reset the adapter — a poisoned #connecting can
  // only be cleared by a fresh adapter instance (the Reload affordance), never from app code.
  const clearSolanaConnecting = useCallback(() => {
    connectingIdRef.current = null
    clearHangTimer()
    setConnectingId(null)
    setConnectingName(null)
    setTakingLong(false)
  }, [clearHangTimer])

  // Begin a fresh Solana connect attempt: reset dedupe, set spinner state, log ATTEMPT, arm the affordance timer.
  const beginSolanaAttempt = useCallback((id: string, walletName: string) => {
    outcomeHandledRef.current = false
    connectingIdRef.current = id
    setConnectingId(id)
    setConnectingName(walletName)
    setError(null)
    setTakingLong(false)
    emitWalletEvent(WALLET_EVENT.ATTEMPT, { chain: 'solana', wallet: walletName })
    startHangTimer(walletName, 'solana')
  }, [startHangTimer])

  // Centralized Solana connect-failure handling. Phantom is multichain: when its ACTIVE account is an
  // EVM-only account (no Solana key), window.phantom.solana.connect() RESOLVES with a null publicKey, so
  // the adapter throws WalletAccountError (name set, message empty). The old handler matched only
  // 'rejected'/'cancelled' and otherwise showed a bare "Connection failed" — leaving just a split-second
  // spinner with no explanation. Surface an actionable message so the user knows to switch accounts.
  const onSolanaConnectError = useCallback((e: any) => {
    // Dedupe: adapter 'error' event + connect().catch can both fire for one failure. First wins.
    if (outcomeHandledRef.current) return
    outcomeHandledRef.current = true
    const name: string = e?.name ?? ''
    const msg = String(e?.message ?? '')
    // Error-category telemetry is emitted once, globally, by the WalletProvider onError sink (main.tsx)
    // so background/autoConnect failures are captured too. Here we only branch the UI on the category.
    const category = categorizeWalletError(e)
    clearHangTimer()
    // Genuine user cancel → reset quietly (no scary banner, back to the wallet list).
    if (category === WALLET_EVENT.USER_REJECTED) {
      clearSolanaConnecting(); return
    }
    if (name === 'WalletAccountError') {
      // Wallet connected but the active account has no Solana address (e.g. Phantom on an EVM-only account).
      setError('This wallet is on a non-Solana account. Open it, switch to (or add) a Solana account, then Retry.')
    } else if (name === 'WalletNotReadyError') {
      setError('This wallet isn’t available. Make sure the extension is installed and unlocked.')
    } else {
      // Phantom (and some wallets) throw a bare generic error — most often literally "Unexpected error" —
      // when a Solana connect fails for an internal reason. The common trigger is provider contention: an
      // EVM wallet already claimed the injected provider in this tab (see main.tsx window.solana note), so
      // Phantom can't open its Solana side. Replace that dead-end blob with an actionable message; pass a
      // genuinely-specific message through unchanged.
      const unhelpful = !msg || /^unexpected error\.?$/i.test(msg.trim())
      setError(
        unhelpful
          ? 'Couldn’t open a Solana connection. If another wallet is already connected in this tab, disconnect it (or tap Reload), then reconnect.'
          : msg,
      )
    }
    // Keep the modal on the connecting screen and show the Retry / Cancel / Reload affordance with the
    // message — instead of nulling connectingId (which previously dropped the user back to the list with
    // no visible error). connectingId stays set so `isConnecting` remains true.
    setTakingLong(true)
  }, [clearHangTimer, clearSolanaConnecting])

  // App-side guarded interactive connect — used ONLY for retry-on-the-already-selected wallet, where
  // WalletProvider's autoConnect effect will NOT re-fire (its deps don't change). On the initial select
  // path WalletProvider owns the connect and we never call this (it would early-return and strand, #80).
  const attemptSolanaConnect = useCallback((adapter: Adapter, walletName: string) => {
    solanaConnectRef.current()
      .then(() => {
        if (outcomeHandledRef.current) return
        // Use the adapter's OWN synchronous getters (React state lags a render). A resolve with neither
        // `connected` nor `connecting` means connect() no-op'd behind the #connecting guard, or failed
        // silently — recover immediately with the affordance instead of stranding the spinner.
        if (!adapter.connected && !adapter.connecting) {
          outcomeHandledRef.current = true
          emitWalletEvent(WALLET_EVENT.STRANDED, { chain: 'solana', wallet: walletName })
          clearHangTimer()
          setError('Connection didn’t complete. Tap Retry, or Reload if it persists.')
          setTakingLong(true)
        }
      })
      .catch((e: any) => onSolanaConnectError(e))
  }, [clearHangTimer, onSolanaConnectError])

  // Solana connect handler. Single-owner de-race (#80):
  //  • New wallet  → select() and let WalletProvider own the (interactive) connect; we only OBSERVE.
  //  • Same wallet → WalletProvider won't re-fire, so drive a guarded app-side connect for the retry.
  const handleSolanaConnect = useCallback(async (walletName: string) => {
    // Active wallet in 'all' mode — skip connection, fire callback directly
    if (chainFamily === 'all' && solanaConnected && activeSolanaWallet?.adapter.name === walletName) {
      onSelectFamily?.('solana')
      onClose()
      return
    }

    beginSolanaAttempt(`solana-${walletName}`, walletName)

    const active = activeSolanaWallet
    if (active?.adapter.name === walletName) {
      // Retry on the already-selected wallet. Guard: never call connect() into an adapter that's already
      // connecting — it no-ops behind the #connecting guard (adapter.js:214), which is exactly the strand.
      if (active.adapter.connecting) {
        emitWalletEvent(WALLET_EVENT.NOOP_EARLY_RETURN, { chain: 'solana', wallet: walletName, reason: 'already_connecting' })
        return // in-flight; the observers (connected / adapter 'error') resolve the outcome
      }
      emitWalletEvent(WALLET_EVENT.RETRY, { chain: 'solana', wallet: walletName })
      attemptSolanaConnect(active.adapter, walletName)
      return
    }

    // Different wallet — select it and let WalletProvider's autoConnect effect own the interactive
    // connect. We do NOT fire our own connect here (it early-returns behind WalletProvider's and strands).
    solanaSelect(walletName as WalletName)
  }, [solanaSelect, onClose, chainFamily, solanaConnected, activeSolanaWallet, onSelectFamily, beginSolanaAttempt, attemptSolanaConnect])

  // Observe the outcome of the connect that WalletProvider drives on the initial-select path (and surface
  // adapter-level errors on the retry path). adapter.js emits 'error' before throwing, so this single
  // listener catches every failure; success flips `solanaConnected` → the close-effect above. This
  // replaces the old app-side drive-effect that fired a redundant connect() and stranded the spinner.
  useEffect(() => {
    if (!connectingId?.startsWith('solana-')) return
    const target = connectingId.replace('solana-', '')
    const adapter = activeSolanaWallet?.adapter
    if (!adapter || adapter.name !== target) return
    const onErr = (e: any) => onSolanaConnectError(e)
    adapter.on('error', onErr)
    return () => { adapter.off('error', onErr) }
  }, [connectingId, activeSolanaWallet, onSolanaConnectError])

  // Recovery affordance actions (non-destructive).
  const handleSolanaRetry = useCallback(() => {
    const walletName = connectingNameRef.current
    if (!walletName) { clearSolanaConnecting(); return }
    emitWalletEvent(WALLET_EVENT.RETRY, { chain: 'solana', wallet: walletName })
    outcomeHandledRef.current = false
    setError(null)
    setTakingLong(false)
    startHangTimer(walletName, 'solana')
    const active = activeSolanaWalletRef.current
    if (active?.adapter.name === walletName) {
      if (!active.adapter.connecting) attemptSolanaConnect(active.adapter, walletName)
      // else: still connecting — leave it; observers will resolve, timer re-armed.
    } else {
      solanaSelect(walletName as WalletName) // re-select → WalletProvider re-owns the connect
    }
  }, [clearSolanaConnecting, startHangTimer, attemptSolanaConnect, solanaSelect])

  const handleSolanaCancel = useCallback(() => {
    const walletName = connectingNameRef.current
    emitWalletEvent(WALLET_EVENT.USER_REJECTED, { chain: 'solana', wallet: walletName, reason: 'user_cancel' })
    outcomeHandledRef.current = true
    // Best-effort disconnect to clear half-connected state (can't clear a poisoned #connecting — see Reload).
    try { disconnectSolana() } catch { /* noop */ }
    clearSolanaConnecting()
    setError(null)
  }, [clearSolanaConnecting, disconnectSolana])

  // Last-resort reset for the rare genuinely-hung provider: disconnect() provably cannot clear a stuck
  // adapter #connecting, so a full reload (fresh adapter instance) is the only real escape.
  const handleSolanaReload = useCallback(() => {
    emitWalletEvent(WALLET_EVENT.RELOAD_RECOVERY, { chain: 'solana', wallet: connectingNameRef.current })
    window.location.reload()
  }, [])

  // ── EVM recovery affordance actions (P0-1) — the EVM peers of the three Solana handlers above. Kept
  // separate rather than folded into them so the shipped Solana path (#80) is untouched. Non-destructive:
  // none of these aborts the in-flight connect, because no EVM connector exposes a way to.
  const handleEvmRetry = useCallback(() => {
    const attempt = evmAttemptRef.current
    if (!attempt) { handleEvmCancelRef.current?.(); return }
    emitWalletEvent(WALLET_EVENT.RETRY, { chain: 'evm', wallet: attempt.displayName })
    setError(null)
    setTakingLong(false)
    void handleEvmConnect(attempt.connectorId, attempt.displayName)
  }, [handleEvmConnect])

  const handleEvmCancel = useCallback(() => {
    emitWalletEvent(WALLET_EVENT.USER_REJECTED, { chain: 'evm', wallet: connectingNameRef.current, reason: 'user_cancel' })
    // Invalidate rather than abort: wagmi has no connect-abort, and the Coinbase SDK's own
    // `Communicator.disconnect()` (which DOES reject its pending request) is only reachable through the
    // connector's `disconnect()`, which would also tear down an existing live connection. Not wired —
    // an explicit user Cancel must not silently disconnect a wallet the user already had connected.
    evmAttemptIdRef.current += 1
    evmAttemptRef.current = null
    // Cleared here but NOT on modal close: cancelling is the user saying they no longer want this
    // connection, so a late success must not then steer the app into the EVM family (P0-5).
    evmConnectPendingRef.current = false
    clearHangTimer()
    setConnectingId(null)
    setConnectingName(null)
    setWcUri(null)
    setError(null)
    setTakingLong(false)
  }, [clearHangTimer])
  // Retry is declared before Cancel (it is the primary action) but needs to fall back to it, so route
  // through a ref instead of reordering the two.
  const handleEvmCancelRef = useRef(handleEvmCancel)
  handleEvmCancelRef.current = handleEvmCancel

  const handleEvmReload = useCallback(() => {
    emitWalletEvent(WALLET_EVENT.RELOAD_RECOVERY, { chain: 'evm', wallet: connectingNameRef.current })
    window.location.reload()
  }, [])

  // Tron connect handler — direct connect when adapter already selected, effect-driven otherwise
  const handleTronConnect = useCallback(async (walletName: string) => {
    // Already connected with this adapter — just signal family selection
    if (chainFamily === 'all' && tronConnected && activeTronWallet?.adapter.name === walletName) {
      onSelectFamily?.('tron')
      onClose()
      return
    }

    setConnectingId(`tron-${walletName}`)
    setConnectingName(walletName)
    setError(null)
    tronConnectFired.current = false

    // If the target adapter is already selected, connect directly
    if (activeTronWallet?.adapter.name === walletName) {
      tronConnectFired.current = true
      try {
        await connectTron()
      } catch (e: any) {
        setError(getDisplayError(e))
        setConnectingId(null); setConnectingName(null)
      }
      return
    }

    // Different adapter — select it; the drive effect connects after state settles
    selectTronWallet(walletName)
  }, [selectTronWallet, connectTron, onClose, chainFamily, tronConnected, activeTronWallet, onSelectFamily])

  // Drive Tron connection after adapter selection propagates (only for cross-adapter switches)
  useEffect(() => {
    if (!connectingId?.startsWith('tron-')) { tronConnectFired.current = false; return }
    if (tronConnectFired.current) return
    const targetName = connectingId.replace('tron-', '')
    if (activeTronWallet?.adapter.name !== targetName) return
    tronConnectFired.current = true
    connectTronRef.current().catch((e: any) => {
      setError(getDisplayError(e))
      setConnectingId(null); setConnectingName(null)
    })
  }, [connectingId, activeTronWallet]) // no connectTron dep — using ref

  const handleCancel = () => {
    setConnectingId(null)
    setConnectingName(null)
    setWcUri(null)
    setError(null)
    setTakingLong(false)
    connectingIdRef.current = null
    outcomeHandledRef.current = true
    if (hangTimerRef.current) { clearTimeout(hangTimerRef.current); hangTimerRef.current = null }
    // Shared Cancel on the plain spinner (any family). For an EVM attempt this must match
    // `handleEvmCancel`, or a late settle would repaint a screen the user already dismissed. No-ops for
    // Solana/Tron — these refs only ever track an EVM attempt.
    evmAttemptIdRef.current += 1
    evmAttemptRef.current = null
    evmConnectPendingRef.current = false
  }

  // WalletConnect QR / deep-link view.
  //  • Desktop: render the QR + an explicit "WalletConnect scanner" label. Users kept scanning the pairing
  //    URI with their wallet's Send / Solana-Pay scanner, which parses `wc:…` as a recipient ADDRESS →
  //    "invalid address". The URI itself is valid (verified) — the label steers them to the right scanner.
  //  • Mobile: you can't scan your own screen, so offer a deep link. Phantom/Solflare register NO
  //    WalletConnect deep-link in the WC registry (only Backpack does), so we fire the raw `wc:` URI and let
  //    the OS route it to whatever WC wallet is installed, with a copy-link fallback for wallets that expose
  //    a manual WalletConnect paste field.
  if (wcUri) {
    const openInWallet = () => {
      emitWalletEvent(WALLET_EVENT.ATTEMPT, { chain: 'solana', wallet: 'WalletConnect', via: 'deeplink' })
      window.location.href = wcUri
    }
    const copyUri = () => {
      navigator.clipboard.writeText(wcUri).then(() => setCopiedAddr(wcUri)).catch(() => {})
    }
    return (
      <Modal show={isOpen} onClose={onClose} title="WalletConnect">
        <div style={{
          padding: 32, display: 'flex', flexDirection: 'column',
          alignItems: 'center', gap: 16,
        }}>
          {isMobileBrowser ? (
            <>
              <button
                onClick={openInWallet}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                  width: '100%', padding: '14px 20px', background: c.ac, color: '#FFFFFF',
                  border: 'none', borderRadius: 12, cursor: 'pointer', fontSize: 15, fontWeight: 600,
                  fontFamily: FONT_BODY,
                }}
              >
                <ExternalLink size={18} /> Open in wallet app
              </button>
              <p style={{ fontSize: 13, color: c.mu, textAlign: 'center', lineHeight: 1.5 }}>
                Opens your WalletConnect-compatible wallet (e.g. Backpack). If nothing opens, copy the link
                and paste it into your wallet&rsquo;s WalletConnect option.
              </p>
              <button
                onClick={copyUri}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                  width: '100%', padding: '12px 20px', background: 'none', color: c.tx,
                  border: `1px solid ${c.dv}`, borderRadius: 12, cursor: 'pointer', fontSize: 14,
                  fontWeight: 600, fontFamily: FONT_BODY,
                }}
              >
                {copiedAddr === wcUri
                  ? <><Check size={16} /> Copied</>
                  : <><Copy size={16} /> Copy connection link</>}
              </button>
            </>
          ) : (
            <>
              <div style={{ background: '#FFFFFF', padding: 16, borderRadius: 14 }}>
                <QRCodeSVG value={wcUri} size={240} />
              </div>
              <p style={{ fontSize: 14, color: c.tx, textAlign: 'center', fontWeight: 600 }}>
                Scan with your wallet&rsquo;s WalletConnect scanner
              </p>
              <p style={{ fontSize: 13, color: c.mu, textAlign: 'center', lineHeight: 1.5 }}>
                Open the WalletConnect option in your mobile wallet &mdash; not the Send or Solana&nbsp;Pay
                scanner, which can&rsquo;t read a WalletConnect code.
              </p>
            </>
          )}
        </div>
      </Modal>
    )
  }

  const isConnecting = !!connectingId
  const title = connectingName ? `Connecting to ${connectingName}\u2026` : 'Connect wallet'

  // Which family owns the in-flight attempt \u2014 decides which set of recovery handlers the affordance wires
  // up. Tron never arms the hang timer, so it is excluded rather than lumped in with EVM.
  const isSolanaAttempt = !!connectingId?.startsWith('solana-')
  const isEvmAttempt = !!connectingId && !isSolanaAttempt && !connectingId.startsWith('tron-')
  // P0-1: the recovery affordance is no longer Solana-only. For a Solana attempt this is exactly the old
  // `connectingId?.startsWith('solana-') && takingLong` condition; EVM now qualifies too.
  const showRecoveryAffordance = takingLong && (isSolanaAttempt || isEvmAttempt)

  // P0-4: `connectingName` is already "Coinbase Wallet" (getConnectorDisplayName, above), so the old
  // literal `{connectingName} wallet` rendered "Open your Coinbase Wallet wallet\u2026" \u2014 confirmed in the
  // shipped bundle. Only append the noun when the name doesn't already carry it, which also keeps
  // "Mobile Wallet Adapter" clean while leaving MetaMask / Phantom / Solflare reading as before.
  // Fixer pass, G-2 \u2014 the noun-doubling guard now lives once, in `appendWalletSuffix`
  // (`utils/walletLabel.ts`), and `WalletChip`'s aria-label reuses it too. Identical regex, identical
  // output for every input this line was already handling \u2014 behaviour-preserving lift, not a rewrite.
  const pendingWalletLabel = connectingName ? appendWalletSuffix(connectingName) : 'wallet'

  // Render EVM wallet rows (shared between 'evm' and 'all' modes)
  const renderEvmRow = (connector: (typeof uniqueConnectors)[number]) => {
    const displayName = getConnectorDisplayName(connector.name)
    const logoKey = WALLET_LOGO_KEY[displayName]
    const LogoComponent = logoKey ? WalletLogos[logoKey] : null
    const desc = WALLET_DESCRIPTIONS[displayName]
    const isThisActive = evmConnected && activeEvmConnector &&
      (activeEvmConnector.id === connector.id || activeEvmConnector.uid === connector.uid)

    return (
      <button
        key={connector.uid}
        onClick={() => handleEvmConnect(connector.uid, displayName)}
        style={{
          display: 'flex', alignItems: 'center', gap: 12, width: '100%',
          padding: '16px 20px', background: 'none', border: 'none',
          cursor: 'pointer', borderBottom: `1px solid ${c.dv}`, fontFamily: FONT_BODY,
        }}
        onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = c.blB }}
        onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = 'none' }}
      >
        <div style={{
          width: 40, height: 40,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
        }}>
          {LogoComponent ? <LogoComponent size={40} /> : <Wallet size={26} color={c.mu} />}
        </div>
        <div style={{ flex: 1, textAlign: 'left' }}>
          <div style={{ fontSize: 15, fontWeight: 600, color: c.tx }}>{displayName}</div>
          {desc && <div style={{ fontSize: 13, color: c.mu, marginTop: 2 }}>{desc}</div>}
        </div>
        {chainFamily === 'all' && isThisActive ? (
          <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontSize: 11, color: '#22c55e', fontWeight: 500, fontFamily: FONT_MONO }}>
              {shortAddr(evmAddress ?? null)}
            </span>
            <span
              role="button" aria-label="Copy address"
              onClick={(e) => { e.stopPropagation(); if (evmAddress) { navigator.clipboard.writeText(evmAddress); setCopiedAddr(evmAddress) } }}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28, borderRadius: 8, cursor: 'pointer', color: c.tx, background: c.iB, border: `1px solid ${c.dv}` }}
            >
              {copiedAddr === evmAddress ? <Check size={15} /> : <Copy size={15} />}
            </span>
            <span
              role="button" aria-label="Disconnect"
              onClick={(e) => { e.stopPropagation(); disconnectEvm() }}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28, borderRadius: 8, cursor: 'pointer', color: c.eC, background: c.iB, border: `1px solid ${c.dv}` }}
            >
              <LogOut size={15} />
            </span>
          </span>
        ) : (
          <ChevronRight size={15} style={{ color: c.mu }} />
        )}
      </button>
    )
  }

  // Ordered EVM rows via EVM_DISPLAY_ORDER: named wallets in curated order (Phantom kept in its slot —
  // ABOVE Coinbase — as the injected connector row OR a deep-link fallback), WalletConnect last. Matches web.
  const evmConnByName = new Map(uniqueConnectors.map(cn => [getConnectorDisplayName(cn.name), cn]))
  const evmOrderedRows = EVM_DISPLAY_ORDER.flatMap(name => {
    const cn = evmConnByName.get(name)
    if (cn) return [renderEvmRow(cn)]
    if (name === 'Phantom' && showPhantomFallback) return [renderWalletFallbackRow('Phantom', 'evm')]
    return []
  })

  // Render one injected Solana wallet row.
  const renderSolanaRow = (wallet: (typeof solanaWallets)[number]) => {
      const logoKey = WALLET_LOGO_KEY[wallet.adapter.name]
      const LogoComponent = logoKey ? WalletLogos[logoKey] : null
      const isThisActiveSolana = solanaConnected && activeSolanaWallet?.adapter.name === wallet.adapter.name
      // Selectable = detected extension (Installed) OR loadable-on-demand (Loadable). The Mobile Wallet
      // Adapter reports `Loadable` (never `Installed`) yet IS usable on mobile, so the old
      // `readyState !== 'Installed'` test wrongly labeled MWA "Not installed" and made it read as disabled.
      // Only genuinely-absent states (NotDetected/Unsupported) show the "Not installed" hint. (#39)
      const isReady = wallet.readyState === WalletReadyState.Installed ||
        wallet.readyState === WalletReadyState.Loadable

      return (
        <button
          key={wallet.adapter.name}
          onClick={() => handleSolanaConnect(wallet.adapter.name)}
          style={{
            display: 'flex', alignItems: 'center', gap: 12, width: '100%',
            padding: '16px 20px', background: 'none', border: 'none',
            cursor: 'pointer', borderBottom: `1px solid ${c.dv}`, fontFamily: FONT_BODY,
          }}
          onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = c.blB }}
          onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = 'none' }}
        >
          <div style={{
            width: 40, height: 40,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}>
            {LogoComponent ? (
              <LogoComponent size={40} />
            ) : wallet.adapter.icon ? (
              <img src={wallet.adapter.icon} alt={wallet.adapter.name} width={40} height={40} style={{ borderRadius: 8 }} />
            ) : (
              <Wallet size={26} color={c.mu} />
            )}
          </div>
          <div style={{ flex: 1, textAlign: 'left' }}>
            <div style={{ fontSize: 15, fontWeight: 600, color: c.tx }}>{wallet.adapter.name}</div>
            {!isReady && (
              <div style={{ fontSize: 13, color: c.mu, marginTop: 2 }}>Not installed</div>
            )}
          </div>
          {chainFamily === 'all' && isThisActiveSolana ? (
            <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 11, color: '#22c55e', fontWeight: 500, fontFamily: FONT_MONO }}>
                {shortAddr(activeSolanaWallet?.adapter.publicKey?.toBase58() ?? null)}
              </span>
              <span
                role="button" aria-label="Copy address"
                onClick={(e) => { e.stopPropagation(); const a = activeSolanaWallet?.adapter.publicKey?.toBase58(); if (a) { navigator.clipboard.writeText(a); setCopiedAddr(a) } }}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28, borderRadius: 8, cursor: 'pointer', color: c.tx, background: c.iB, border: `1px solid ${c.dv}` }}
              >
                {copiedAddr === activeSolanaWallet?.adapter.publicKey?.toBase58() ? <Check size={15} /> : <Copy size={15} />}
              </span>
              <span
                role="button" aria-label="Disconnect"
                onClick={(e) => { e.stopPropagation(); disconnectSolana() }}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28, borderRadius: 8, cursor: 'pointer', color: c.eC, background: c.iB, border: `1px solid ${c.dv}` }}
              >
                <LogOut size={15} />
              </span>
            </span>
          ) : (
            <ChevronRight size={15} style={{ color: c.mu }} />
          )}
        </button>
      )
  }

  // Solana section order: MetaMask → Phantom → Solflare → Backpack (each an injected row OR a deep-link
  // fallback) → any other injected wallets → [WalletConnect last — #57].
  const solWalletByName = new Map(solanaWallets.map(w => [String(w.adapter.name), w]))
  const solLeadRows = (['MetaMask', 'Phantom', 'Solflare', 'Backpack'] as const).flatMap(name => {
    const w = solWalletByName.get(name)
    if (w) return [renderSolanaRow(w)]
    if (name === 'Phantom' && showPhantomSolanaFallback) return [renderWalletFallbackRow('Phantom', 'solana')]
    if (name === 'MetaMask' && showMetaMaskSolanaFallback) return [renderWalletFallbackRow('MetaMask', 'solana')]
    if (name === 'Solflare' && showSolflareSolanaFallback) return [renderWalletFallbackRow('Solflare', 'solana')]
    if (name === 'Backpack' && showBackpackSolanaFallback) return [renderWalletFallbackRow('Backpack', 'solana')]
    return []
  })
  // Other injected Solana wallets go in the middle; WalletConnect is pulled out and rendered LAST (#57),
  // matching the EVM section where WalletConnect ("Use any other wallet") is the final row.
  const solLeadNames = new Set(['MetaMask', 'Phantom', 'Solflare', 'Backpack', 'WalletConnect'])
  const solOtherRows = solanaWallets
    .filter(w => !solLeadNames.has(String(w.adapter.name)))
    .map(renderSolanaRow)
  const solWcWallet = solWalletByName.get('WalletConnect')
  const solWcRow = solWcWallet ? renderSolanaRow(solWcWallet) : null
  const solHasAnyRow = solLeadRows.length > 0 || solOtherRows.length > 0 || solWcRow !== null

  // Render Tron wallet rows (multi-adapter: TronLink + WalletConnect).
  // Mirror the Solana/Phantom pattern: ALWAYS list TronLink (never filter it out when the
  // extension isn't detected). If it isn't installed, show a "Not installed" hint and send the
  // click to the install page instead of attempting (and failing) a connect.
  const tronRows = tronWallets.length > 0 ? (
    tronWallets
      .map(w => {
        const isWalletConnect = w.adapter.name === 'WalletConnect'
        const isThisActiveTron = tronConnected && activeTronWallet?.adapter.name === w.adapter.name
        const notInstalled = !isWalletConnect && w.adapter.readyState !== 'Found'
        const displayName = isWalletConnect ? 'WalletConnect' : w.adapter.name
        const logoKey = WALLET_LOGO_KEY[displayName]
        const LogoComponent = logoKey ? WalletLogos[logoKey] : null

        return (
          <button
            key={w.adapter.name}
            onClick={() => notInstalled
              ? window.open(TRONLINK_INSTALL_URL, '_blank', 'noopener,noreferrer')
              : handleTronConnect(w.adapter.name)}
            style={{
              display: 'flex', alignItems: 'center', gap: 12, width: '100%',
              padding: '16px 20px', background: 'none', border: 'none',
              cursor: 'pointer', borderBottom: `1px solid ${c.dv}`, fontFamily: FONT_BODY,
            }}
            onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = c.blB }}
            onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = 'none' }}
          >
            <div style={{
              width: 40, height: 40, borderRadius: 10,
              background: '#FF001318',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0,
            }}>
              {LogoComponent ? (
                <LogoComponent size={40} />
              ) : w.adapter.icon ? (
                <img src={w.adapter.icon} alt={displayName} width={40} height={40} style={{ borderRadius: 8 }} />
              ) : (
                <Wallet size={26} color={c.mu} />
              )}
            </div>
            <div style={{ flex: 1, textAlign: 'left' }}>
              <div style={{ fontSize: 15, fontWeight: 600, color: c.tx }}>{displayName}</div>
              {isWalletConnect ? (
                <div style={{ fontSize: 13, color: c.mu, marginTop: 2 }}>Use any Tron wallet</div>
              ) : notInstalled ? (
                <div style={{ fontSize: 13, color: c.mu, marginTop: 2 }}>Not installed</div>
              ) : null}
            </div>
            {chainFamily === 'all' && isThisActiveTron ? (
              <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ fontSize: 11, color: '#22c55e', fontWeight: 500, fontFamily: FONT_MONO }}>
                  {shortAddr(tronAddress)}
                </span>
                <span
                  role="button" aria-label="Copy address"
                  onClick={(e) => { e.stopPropagation(); if (tronAddress) { navigator.clipboard.writeText(tronAddress); setCopiedAddr(tronAddress) } }}
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28, borderRadius: 8, cursor: 'pointer', color: c.tx, background: c.iB, border: `1px solid ${c.dv}` }}
                >
                  {copiedAddr === tronAddress ? <Check size={15} /> : <Copy size={15} />}
                </span>
                <span
                  role="button" aria-label="Disconnect"
                  onClick={(e) => { e.stopPropagation(); disconnectTron() }}
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28, borderRadius: 8, cursor: 'pointer', color: c.eC, background: c.iB, border: `1px solid ${c.dv}` }}
                >
                  <LogOut size={15} />
                </span>
              </span>
            ) : (
              <ChevronRight size={15} style={{ color: c.mu }} />
            )}
          </button>
        )
      })
  ) : (
    <div style={{ textAlign: 'center', padding: '32px 20px' }}>
      <p style={{ fontSize: 14, color: c.mu }}>
        No Tron wallets detected.{' '}
        <a
          href="https://www.tronlink.org/"
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: c.ac, textDecoration: 'none' }}
        >
          Install TronLink
        </a>
      </p>
    </div>
  )

  return (
    /* P0-2: `closable` is no longer gated on `isConnecting`. Backdrop / Esc / X were all disabled while a
       connect was in flight, which — combined with a connect that never settles (P0-1) — left the user
       staring at a spinner with no way out. The close path resets every piece of connect state. */
    <Modal show={isOpen} onClose={onClose} title={title}>
      {isConnecting ? (
        /* Dedicated loading screen */
        showRecoveryAffordance ? (
          /* #80 non-destructive recovery affordance — shown after the hang threshold, a strand, or an
             actionable error. We never auto-abort (WalletConnect connects can legitimately run long).
             P0-1: EVM attempts now reach this too, wired to their own handlers. */
          <div style={{ padding: '32px 20px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14 }}>
            <div style={{ fontSize: 15, fontWeight: 600, color: c.tx, fontFamily: FONT_HEADING }}>
              Taking longer than expected
            </div>
            {/* P0-6: this is where a failed connect's message actually lands, so it has to be announced —
                an error a screen reader never reads out is only half-surfaced. Assertive ONLY when there
                is a real error; the "still waiting" copy is a status, and interrupting a screen-reader
                user to tell them nothing has happened yet is worse than staying quiet. */}
            <div
              role={error ? 'alert' : 'status'}
              aria-live={error ? 'assertive' : 'polite'}
              style={{ fontSize: 13, color: c.mu, textAlign: 'center', maxWidth: 300, lineHeight: 1.5 }}
            >
              {error || `Still waiting on ${connectingName || 'your wallet'}. Retry, or reload the page if it keeps hanging.`}
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 4, flexWrap: 'wrap', justifyContent: 'center' }}>
              <button
                onClick={isSolanaAttempt ? handleSolanaRetry : handleEvmRetry}
                style={{
                  padding: '8px 16px', borderRadius: 10, border: 'none', background: c.ac,
                  color: '#fff', fontSize: 13, fontWeight: 600, fontFamily: FONT_BODY, cursor: 'pointer',
                }}
              >
                Retry
              </button>
              <button
                onClick={isSolanaAttempt ? handleSolanaReload : handleEvmReload}
                style={{
                  padding: '8px 16px', borderRadius: 10, border: `1px solid ${c.dv}`, background: 'none',
                  color: c.tx, fontSize: 13, fontWeight: 500, fontFamily: FONT_BODY, cursor: 'pointer',
                }}
              >
                Reload
              </button>
              <button
                onClick={isSolanaAttempt ? handleSolanaCancel : handleEvmCancel}
                style={{
                  padding: '8px 16px', borderRadius: 10, border: `1px solid ${c.dv}`, background: 'none',
                  color: c.ts, fontSize: 13, fontWeight: 500, fontFamily: FONT_BODY, cursor: 'pointer',
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
        <div style={{ padding: '32px 20px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14 }}>
          <Loader2 size={36} style={{ color: c.ac, animation: 'spin 1s linear infinite' }} />
          <div style={{ fontSize: 15, fontWeight: 600, color: c.tx, fontFamily: FONT_HEADING }}>
            Waiting for approval
          </div>
          <div style={{ fontSize: 13, color: c.mu, textAlign: 'center', maxWidth: 300, lineHeight: 1.5 }}>
            Open your {pendingWalletLabel} and approve the connection to continue.
          </div>
          {error && (
            /* P0-6: announce the failure instead of only drawing it. */
            <div role="alert" aria-live="assertive" style={{
              padding: 10, borderRadius: 10, background: c.eB,
              border: `1px solid ${c.eC}33`, color: c.eC,
              fontSize: 13, textAlign: 'center', width: '100%',
            }}>
              {error}
            </div>
          )}
          <button
            onClick={handleCancel}
            style={{
              marginTop: 8, padding: '8px 16px', borderRadius: 10,
              border: `1px solid ${c.dv}`, background: 'none',
              color: c.ts, fontSize: 13, fontWeight: 500, fontFamily: FONT_BODY,
              cursor: 'pointer',
            }}
          >
            Cancel
          </button>
        </div>
        )
      ) : (
        /* Wallet list — full-width rows */
        <div>
          {(chainFamily === 'all' || chainFamily === 'evm') && (
            <>
              {chainFamily === 'all' && <SectionHeader label="EVM Wallets" subtitle="Ethereum, Base, Polygon, etc." color={c.mu} />}
              {evmOrderedRows}
            </>
          )}
          {(chainFamily === 'all' || chainFamily === 'solana') && (
            <>
              {chainFamily === 'all' && <SectionHeader label="Solana" color={c.mu} />}
              {solLeadRows}
              {solOtherRows}
              {solWcRow}
              {!solHasAnyRow && (
                <div style={{ textAlign: 'center', padding: '32px 20px' }}>
                  <p style={{ fontSize: 14, color: c.mu }}>
                    No Solana wallets detected. Install Phantom or another Solana wallet.
                  </p>
                </div>
              )}
            </>
          )}
          {(chainFamily === 'all' || chainFamily === 'tron') && (
            <>
              {chainFamily === 'all' && <SectionHeader label="Tron" color={c.mu} />}
              {tronRows}
            </>
          )}

          {/* Recipient-only: paste a custom address instead of connecting a wallet. Rendered LAST (after
              WalletConnect) and only when the parent supplies the handler — i.e. the destination context.
              Hands off to the RecipientModal, so a user who opened the wallet list can still reach the
              paste-address path (and vice-versa) — the recipient is always changeable (#107). */}
          {onEnterCustomAddress && (
            <button
              onClick={onEnterCustomAddress}
              style={{
                display: 'flex', alignItems: 'center', gap: 12, width: '100%',
                padding: '16px 20px', background: 'none', border: 'none',
                cursor: 'pointer', borderTop: `1px solid ${c.dv}`, fontFamily: FONT_BODY,
              }}
              onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = c.blB }}
              onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = 'none' }}
            >
              <div style={{
                width: 40, height: 40, borderRadius: '50%',
                background: 'rgba(34, 197, 94, 0.12)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              }}>
                <Pencil size={18} color={c.ac} />
              </div>
              <div style={{ flex: 1, textAlign: 'left' }}>
                <div style={{ fontSize: 15, fontWeight: 600, color: c.tx }}>Enter a custom address</div>
                <div style={{ fontSize: 13, color: c.mu, marginTop: 2 }}>Send to any address you paste</div>
              </div>
              <ChevronRight size={15} style={{ color: c.mu }} />
            </button>
          )}
        </div>
      )}
    </Modal>
  )
}
